/** @format */

import { Box, FormControlLabel, Grid } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { FormikProps } from 'formik';
import * as React from 'react';
import { useAppSelector } from '../../app/hooks';
import { isCheckboxDisabledAtMax } from '../Logic/maxSelectionCheckbox';
import { selectConstants } from './Api/constantSlice';
import { DropdownDto } from './Api/Models';

interface Props {
  formik: FormikProps<any>;
  /** Unchecked options are disabled once this many universities are selected. */
  maxSelection: number;
  /**
   * If set, only these institutions are shown, in this order (e.g. PGDA HDI:
   * Zululand then Venda).
   */
  restrictToInstitutionIds?: readonly number[];
  /**
   * If set (and `restrictToInstitutionIds` is not), show all universities except these ids
   * (e.g. 4th year HDI: hide Zululand, Venda, Limpopo so the applicant picks other institutions).
   */
  excludeInstitutionIds?: readonly number[];
  /** Checked rows matching these ids cannot be unchecked (e.g. current HDI uni). */
  lockedInstitutionIds?: readonly number[];
  /** When true, show the current selection as read-only (no checkboxes). */
  readOnlySelection?: boolean;
}
export default function UniversityMultiselect({
  formik,
  maxSelection,
  restrictToInstitutionIds,
  excludeInstitutionIds,
  lockedInstitutionIds,
  readOnlySelection,
}: Props) {
  const selData = useAppSelector(selectConstants);

  const institutions: DropdownDto[] = Array.isArray(formik.values.institutions)
    ? formik.values.institutions
    : [];

  const isChecked = (data: DropdownDto) => {
    const index = institutions.findIndex(
      (s: DropdownDto) => s.id === data.id
    );

    if (index > -1) return true;
    return false;
  };

  const selectedCount = institutions.length;

  const isLocked = (institutionId: number) =>
    Boolean(lockedInstitutionIds?.includes(institutionId));

  const allUniversities = selData.constantState?.universities ?? [];
  const listToRender: DropdownDto[] = restrictToInstitutionIds?.length
    ? restrictToInstitutionIds
        .map((id) => allUniversities.find((u) => u.id === id))
        .filter((u): u is DropdownDto => u != null)
    : excludeInstitutionIds?.length
      ? allUniversities.filter(
          (u) => !(excludeInstitutionIds as readonly number[]).includes(u.id)
        )
      : allUniversities.filter(
          (s) => s.id !== 30 && s.id !== 3 && s.id !== 23 && s.id !== 25
        );

  if (readOnlySelection) {
    return (
      <Box sx={{ marginLeft: 2 }}>
        <Box className='error_material'>
          {(formik.touched.institutions || formik.submitCount > 0) &&
            (formik.errors.institutions as React.ReactNode)}
        </Box>
        <Grid container spacing={1} mb={2}>
          {institutions.map((u) => (
            <Grid item xs={12} key={u.id}>
              <Box component='span' sx={{ fontWeight: 600 }}>
                {u.description}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ marginLeft: 2 }}>
      <Box className='error_material'>
        {(formik.touched.institutions || formik.submitCount > 0) &&
          (formik.errors.institutions as React.ReactNode)}
      </Box>
      <Grid container spacing={2} mb={3}>
        {listToRender.map((value: DropdownDto) => {
            const labelId = `checkbox-list-label-${value.id}`;
            const checked = isChecked(value);
            return (
              <Grid item xs={6} md={3} key={value.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      edge='start'
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                      checked={checked}
                      disabled={isCheckboxDisabledAtMax(
                        checked,
                        selectedCount,
                        maxSelection
                      )}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        const universities = [...institutions];

                        if (event.target.checked) {
                          if (
                            maxSelection > 0 &&
                            universities.length >= maxSelection
                          ) {
                            return;
                          }
                          universities.push(value);
                        } else {
                          if (isLocked(value.id)) return;
                          const i = universities.findIndex(
                            (s: DropdownDto) => s.id === value.id
                          );
                          if (i === -1) return;
                          universities.splice(i, 1);
                        }

                        formik.setFieldValue('institutions', universities);
                      }}
                    />
                  }
                  label={value.description}
                />
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
}
